// Activity Door builds, held OUTSIDE the React tree that displays them.
//
// A door build takes 6–9 minutes on Opus (measured: 30k–53k output tokens per
// build) and costs real credits — ~816 for the card-sort build that prompted
// this module. It used to live entirely in `GeneratedInteractiveSlide`'s local
// state, with an unmount cleanup that aborted the in-flight stream. Every way
// out of that component therefore destroyed the build silently:
//
//   - the builder's Edit|Preview segment early-returns a DIFFERENT tree
//     (SlideEditor's immersive preview), so toggling it unmounts the canvas;
//   - the canvas is keyed on the selected slide id, so clicking another slide
//     in the strip unmounts it too.
//
// The server does not stop when the client hangs up: it runs to completion and
// commits the debit. So the observed failure was an author waiting four minutes
// on a spinner with no visible progress, clicking Preview to see whether
// anything had happened, and thereby throwing away a build that landed on the
// server two minutes later. It re-rendered as "Briefed — not built yet" with no
// error anywhere — indistinguishable from "it got stuck".
//
// The run therefore lives here, keyed by slide id, and the component is a
// subscriber. Unmounting unsubscribes; only an explicit Cancel aborts. A result
// that lands while nothing is mounted waits in the registry and is applied when
// the slide is next shown, so wandering off mid-build costs nothing.

import {
  streamDoorBuild,
  DoorBuildError,
  type DoorStreamOptions,
} from '../../services/doorBuildService';
import { parseDoorBuildOutput, parseRefineOutput, partialHtml } from './parseBuildOutput';

/**
 * `Omit` over a union collapses it to the keys the members share, which would
 * silently drop `door`/`previousHtml` — distribute it instead.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** What a caller supplies: everything except the stream plumbing this owns. */
export type DoorRunOptions = DistributiveOmit<
  DoorStreamOptions,
  'signal' | 'onText' | 'onThinking'
>;

/**
 * Opus 5 thinks before it writes, and thinking emits no HTML — so a build
 * spends its first stretch producing nothing the author can see. The phase is
 * carried through to the UI precisely so that stretch reads as work rather
 * than as a hang.
 */
export type DoorRunPhase = 'thinking' | 'writing';

export interface DoorRunState {
  /** Monotonic id — the component uses it to apply each result exactly once. */
  token: number;
  /** 'restyle' is a refine whose request is a new world — see `world`. */
  mode: 'build' | 'refine' | 'restyle';
  status: 'running' | 'done' | 'error' | 'no-fit';
  phase: DoorRunPhase;
  /** Wall-clock start, so the UI can show elapsed time. */
  startedAt: number;
  /** mode 'restyle' — the world being moved into. The slide's `intake.world`
   *  is only committed when the restyle lands, so until then this is the one
   *  record of where it is going (and it survives the slide unmounting). */
  world?: string;
  /** Characters of reasoning seen before the document started. */
  thoughtChars: number;
  /** Characters of the document written so far. */
  writtenChars: number;
  /** Throttled paintable partial document, or null before the doctype lands. */
  preview: string | null;
  /** status 'done' — the finished build, awaiting application to the slide. */
  result?: { html: string; note: string | null; model: string };
  /** status 'error' — a message the author can act on. */
  error?: string;
  /** status 'no-fit' — the door declined the brief, and why. */
  verdict?: string;
}

type Listener = (state: DoorRunState | undefined) => void;

interface Run {
  state: DoorRunState;
  controller: AbortController;
  /** Last notification, so token-rate updates don't re-render per chunk. */
  lastNotifiedAt: number;
  /** Last preview repaint — srcDoc swaps reload the iframe, so throttle hard. */
  lastPaintedAt: number;
}

/** Repaint the streaming preview at most this often. */
const PAINT_INTERVAL_MS = 2000;
/** Coalesce progress notifications — deltas arrive far faster than this. */
const NOTIFY_INTERVAL_MS = 250;

const runs = new Map<string, Run>();
/** Subscribers are keyed by SLIDE, not by run, so a component can watch a slide
 *  whose build has not started yet — and keep watching across runs. */
const listeners = new Map<string, Set<Listener>>();
let nextToken = 1;

/** Push a state to everyone watching this slide. */
function announce(slideId: string, state: DoorRunState | undefined): void {
  for (const listener of listeners.get(slideId) ?? []) listener(state);
}

/**
 * Is this run still the one the slide belongs to?
 *
 * A cancelled or replaced run keeps streaming for a moment — an abort takes
 * effect at the next read, and callbacks already queued still fire — but
 * subscribers are keyed by SLIDE, so anything it announces reaches whoever is
 * watching the run that replaced it. Two ways that bites: stale progress
 * resets the live build's counters, and a run that wins the race between the
 * abort and its own promise settling reports `done` on the success path, which
 * has no abort check at all — applying a build the author already threw away.
 *
 * So a run that is no longer current is inert. Nothing it does is announced,
 * and nothing it produces is kept.
 */
function isCurrent(slideId: string, run: Run): boolean {
  return runs.get(slideId) === run;
}

/** Advance a run's state and, subject to the throttle, tell its watchers. */
function patch(
  slideId: string,
  run: Run,
  next: Partial<DoorRunState>,
  { force = false } = {},
): void {
  if (!isCurrent(slideId, run)) return;

  // The two changes worth interrupting the throttle for: leaving the thinking
  // phase, and a new frame of the document. They are the moments the author is
  // waiting on, and holding either back by up to NOTIFY_INTERVAL_MS is exactly
  // the wrong quarter-second to be economical in.
  const notable =
    (next.phase !== undefined && next.phase !== run.state.phase) ||
    (next.preview !== undefined && next.preview !== run.state.preview);

  // A fresh object every time: subscribers are React components comparing by
  // identity, and a mutated-in-place state would never look changed.
  run.state = { ...run.state, ...next };

  const now = Date.now();
  if (!force && !notable && now - run.lastNotifiedAt < NOTIFY_INTERVAL_MS) return;
  run.lastNotifiedAt = now;
  announce(slideId, run.state);
}

/** The slide's current run, if it has one. A mounting component reads this
 *  before subscribing, so a build already in flight shows up immediately. */
export function getDoorRun(slideId: string): DoorRunState | undefined {
  return runs.get(slideId)?.state;
}

/** Watch a slide's runs. Fires on every state change until unsubscribed. */
export function watchDoorRun(slideId: string, listener: Listener): () => void {
  let set = listeners.get(slideId);
  if (!set) {
    set = new Set();
    listeners.set(slideId, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(slideId);
  };
}

/** True while a build or refine is in flight for this slide. */
export function isDoorRunActive(slideId: string): boolean {
  return runs.get(slideId)?.state.status === 'running';
}

/**
 * Start a run for `slideId`. Idempotent while one is in flight — a second call
 * (a React StrictMode double-mount, an impatient second click) is a no-op
 * rather than a second debit.
 */
export function startDoorRun(slideId: string, options: DoorRunOptions): void {
  if (isDoorRunActive(slideId)) return;

  const controller = new AbortController();
  const run: Run = {
    state: {
      token: nextToken++,
      mode: options.mode,
      ...(options.mode === 'restyle' ? { world: options.world } : {}),
      status: 'running',
      phase: 'thinking',
      startedAt: Date.now(),
      thoughtChars: 0,
      writtenChars: 0,
      preview: null,
    },
    controller,
    lastNotifiedAt: 0,
    lastPaintedAt: 0,
  };
  runs.set(slideId, run);
  announce(slideId, run.state);

  void (async () => {
    try {
      const result = await streamDoorBuild({
        ...options,
        signal: controller.signal,
        onThinking: (chars: number) => {
          if (run.state.phase !== 'thinking') return;
          patch(slideId, run, { thoughtChars: chars });
        },
        onText: (accumulated: string) => {
          const now = Date.now();
          const doc = partialHtml(accumulated);
          const repaint = doc !== null && now - run.lastPaintedAt >= PAINT_INTERVAL_MS;
          if (repaint) run.lastPaintedAt = now;
          patch(slideId, run, {
            phase: 'writing',
            writtenChars: accumulated.length,
            ...(repaint ? { preview: doc } : {}),
          });
        },
      } as DoorStreamOptions);

      // Cancelled or replaced while the last of the stream was in flight: the
      // result belongs to a run nobody is waiting for any more.
      if (!isCurrent(slideId, run)) return;

      if (options.mode === 'build') {
        const parsed = parseDoorBuildOutput(result.fullText);
        if (parsed.kind === 'no-fit') {
          patch(
            slideId,
            run,
            { status: 'no-fit', verdict: parsed.verdict, preview: null },
            { force: true },
          );
          return;
        }
        patch(
          slideId,
          run,
          {
            status: 'done',
            preview: null,
            result: { html: parsed.htmlContent, note: parsed.inventedNote, model: result.model },
          },
          { force: true },
        );
        return;
      }

      const parsed = parseRefineOutput(result.fullText);
      patch(
        slideId,
        run,
        {
          status: 'done',
          preview: null,
          result: { html: parsed.htmlContent, note: parsed.changeNote, model: result.model },
        },
        { force: true },
      );
    } catch (e) {
      // An abort is only ever the author's own Cancel, which already dropped
      // the run — reporting an error over it would contradict what they did.
      if (controller.signal.aborted) return;
      const message =
        e instanceof DoorBuildError
          ? e.message
          : options.mode === 'refine'
            ? 'The tweak failed. Try again.'
            : options.mode === 'restyle'
              ? 'The restyle failed. Nothing was changed — try again.'
              : 'The build failed. Try again.';
      patch(slideId, run, { status: 'error', error: message, preview: null }, { force: true });
    }
  })();
}

/** Explicit author cancel — the ONE path that aborts an in-flight stream. */
export function cancelDoorRun(slideId: string): void {
  const run = runs.get(slideId);
  if (!run) return;
  run.controller.abort();
  runs.delete(slideId);
  announce(slideId, undefined);
}

/** Drop a settled run — called once its result or error has been taken up. */
export function clearDoorRun(slideId: string): void {
  const run = runs.get(slideId);
  if (!run || run.state.status === 'running') return;
  runs.delete(slideId);
  announce(slideId, undefined);
}

/** Test seam — the registry is module state that would otherwise leak between specs. */
export function __resetDoorRuns(): void {
  for (const run of runs.values()) run.controller.abort();
  runs.clear();
  listeners.clear();
  nextToken = 1;
}
